import Form, {TradeInForm} from '../../components/form';
import {Accordion} from '../../components/accordion';
import {ButtonShare} from '../../components/button-share';
import CompareButton from '../../components/compare-button';
import CompareList from '../../sections/compare';
import CompareState from '../../components/compare-state';
import CustomSelect from '../../components/form/custom-select';
import FavoriteButton from '../../components/favorite-button';
import FavoritesList from '../../components/favorites';
import FavoriteState from '../../components/favorite-state';
import FlatType from '../../components/flat-type';
import MultiStepForm from '../../sections/opk';
import NewsList from '../../sections/news-list';
import ObjectSlider from '../../components/flat-card';
import Observer from './observer';
import ParallaxColumn from '../../components/parallax/parallax-column';
import Parametric from '../../components/parametric';
import PopupFacade from '../../components/popup/popupFacade';
import {PostMessagePopup} from '../../components/popup/popup';
import Progress from '../../sections/progress';
import PromotionsTerms from '../../components/promotions-terms';
import ScrollBy from '../../components/scrollBy';
import Share from '../../components/share';
import {StickyButton} from '../../sections/sticky-btn';
import Stock from '../../components/stock';
import SwiperSlider from '../../components/slider-swiper';
import Tabs from '../../components/tabs';
import {throttle} from 'throttle-debounce';
import Toggle from '../../components/toggle';
import {Utils} from './utils';
import Validation from '../../components/form/validation';

const validation = new Validation();
const observer = new Observer();

/* Подключение ленивой загрузки (img/picture/iframe) */
export const lazyLoadElements = (lazyElements) => {
    const replaceSource = (element) => {
        if (element.parentElement.tagName === 'PICTURE' && element.tagName === 'IMG') {
            element.addEventListener('load', () => {
                element.parentElement.style.backgroundImage = '';
            });
        }

        const targetAttribute = element.nodeName === 'SOURCE' ? 'srcset' : 'src';
        const sourceElement = element.nodeName === 'SOURCE' ? 'data-srcset' : 'data-src';

        if (element.getAttribute(sourceElement) !== null) {
            element.setAttribute(targetAttribute, element.getAttribute(sourceElement));
            element.removeAttribute(sourceElement);
        }
    };

    if ('IntersectionObserver' in window) {
        // Иначе по событию IntersectionObserver
        const lazyImageObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const lazyEl = entry.target;

                    replaceSource(lazyEl);
                    lazyImageObserver.unobserve(lazyEl);
                }
            });
        });

        lazyElements.forEach((lazyImage) => {
            if (Utils.isInViewport(lazyImage, window.innerHeight || document.documentElement.clientHeight)) {
                replaceSource(lazyImage);
            } else {
                lazyImageObserver.observe(lazyImage);
            }
        });
    } else {
        // Если есть нативная поддержка атрибута или нет поддержки IntersectionObserver - подменяем сразу
        lazyElements.forEach((lazyEl) => {
            replaceSource(lazyEl);
        });
    }
};

export const initPopup = (buttons, otherOptions = {}) => {
    const options = {
        ...{openButtons: buttons},
        ...otherOptions
    };

    new PopupFacade(options).init();
};

export const initVideoTabs = (wrapperSelector) => {
    const wrapper = document.querySelector(wrapperSelector);

    if (wrapper) {
        const tabs = Array.from(wrapper.querySelectorAll('.j-video__tab'));
        const iframe = wrapper.querySelector('.j-video__iframe');
        const isActive = 'is-active';

        tabs.forEach((tab) => {
            tab.addEventListener('click', (event) => {
                const target = event.currentTarget;

                iframe.src = target.dataset.video;
                tabs.forEach((element) => {
                    const method = element === target ? 'add' : 'remove';

                    element.classList[method](isActive);
                });
            });
        });
    }
};

// Установка размеров видео первого экрана
export const initFullVideo = (fullIframe) => {
    const wrapper = document.querySelector('.j-first-screen__video');
    const ratio = 0.5625;

    const setVideoSize = () => {
        const width = Utils.getWindowWidth();
        const height = Utils.isMobile() ? wrapper.offsetHeight : Utils.getWindowHeight();
        const dimension = height / width;

        if (dimension > ratio) {
            fullIframe.style.width = `${height / ratio}px`;
            fullIframe.style.height = `${height + Math.abs(width - (height / ratio))}px`;
            fullIframe.style.top = `${0.5 * (width - (height / ratio))}px`;
            fullIframe.style.left = `${0.5 * (width - (height / ratio))}px`;
        } else {
            fullIframe.style.width = `${width}px`;
            fullIframe.style.height = `${(width * ratio) + (height * ratio)}px`;
            fullIframe.style.top = `${-Math.abs(0.5 * (height * ratio))}px`;
            fullIframe.style.left = 0;
        }
    };

    const events = ['resize', 'orientationchange'];

    events.forEach((event) => {
        window.addEventListener(event, setVideoSize);
    });

    setVideoSize();
};

export const initYoutubePlayer = (wrapper) => {
    const tag = document.createElement('script');
    const firstScriptTag = document.getElementsByTagName('script')[0];
    let player = null;

    tag.src = 'https://www.youtube.com/iframe_api';
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // eslint-disable-next-line func-names
    window.onYouTubeIframeAPIReady = function() {
        // eslint-disable-next-line no-undef
        player = new YT.Player(wrapper, {
            videoId   : `${wrapper.dataset.id}`,
            playerVars: {
                modestbranding: 1,
                autoplay      : 1,
                mute          : 1,
                controls      : 0,
                showinfo      : 0,
                wmode         : 'transparent',
                branding      : 0,
                rel           : 0,
                autohide      : 1,
                origin        : window.location.origin
            },
            events: {
                // eslint-disable-next-line
                'onReady': onPlayerReady,
                // eslint-disable-next-line
                'onStateChange': onStateChange
            }
        });

        // eslint-disable-next-line func-style
        function onPlayerReady(event) {
            // повторный initFullVideo() уже после инита плеера + ресайз
            const youtubeIframe = document.querySelector('.j-first-screen__youtube');

            initFullVideo(youtubeIframe);
            event.target.playVideo();

            const resizeEvents = ['resize', 'orientationchange'];

            resizeEvents.forEach((resizeEvent) => {
                window.addEventListener(resizeEvent, () => {
                    initFullVideo(youtubeIframe);
                });
            });
        }

        // eslint-disable-next-line func-style
        function onStateChange(event) {
            if (!event.data) {
                player.playVideo();
            }
        }
    };
};

// Инициализация форм
export const initForm = (wrapper) => {
    new Form().init({
        target        : wrapper,
        successMessage: true
    });
};

export const initTradeInForm = (wrapper) => {
    new TradeInForm().init({
        target        : wrapper,
        successMessage: true
    });
};

// Инициализация формы обратной связи в попапах
export const initCallbackForm = () => {
    observer.subscribe('popup:open', (popup) => {
        if (!popup.content) {
            return;
        }

        // Форма
        const callbackElement = popup.content.querySelector('.j-form:not([data-init])');

        if (callbackElement) {
            validation.refresh(callbackElement);

            new Form().init({
                target        : callbackElement,
                successMessage: true,
                popupInstance : popup
            });
            callbackElement.dataset.init = 'true';
        }

        // Аккордеоны
        const accordions = Array.from(popup.content.querySelectorAll('.j-accordion'));

        accordions.forEach((elem) => {
            new Accordion().init({
                elem,
                closeTimeout: true,
                selectors   : {
                    toggler     : '.j-accordion-header',
                    content     : '.j-accordion-content',
                    contentOuter: '.j-accordion-content-outer',
                    title       : '.j-accordion-title',
                    blockElement: '.j-flats-block-desktop'
                }
            });
        });

        // Табы
        const tabs = Array.from(popup.content.querySelectorAll('.j-tabs-wrap'));

        tabs.forEach((elem) => {
            new Tabs().init({elem});
        });
    });
};

// Инициализация попапов с формой обратной связи
export const initCallbackPopup = (buttons) => {
    const options = {
        removeDelay         : 300,
        closeButtonAriaLabel: 'Закрыть форму обратного звонка',
        modify              : 'popup_theme_callback'
    };

    initCallbackForm();
    initPopup(buttons, options);
};

// Инициализация попапов службы доверия
export const initTrustServicePopup = (buttons) => {
    const options = {
        removeDelay         : 300,
        closeButtonAriaLabel: 'Закрыть',
        modify              : 'popup_theme_trust-service'
    };

    initCallbackForm();
    initPopup(buttons, options);
};

// Табы
export const initTabs = (elem) => {
    new Tabs().init({elem});
};

// Табы в табах
export const initTabsInner = (options) => {
    new Tabs().init(options);
};

// Табы ипотеки
export const initMortgageTabs = (options) => {
    new Tabs().init(options);
};

// Аккордеон
export const initAccordions = (elem) => {
    new Accordion().init({
        elem,
        closeTimeout: true,
        selectors   : {
            toggler     : '.j-accordion-header',
            content     : '.j-accordion-content',
            contentOuter: '.j-accordion-content-outer',
            title       : '.j-accordion-title',
            blockElement: '.j-flats-block-desktop'
        }
    });
};

// Аккордеон в ипотечном
export const initMortgageAccordions = (elem) => {
    new Accordion().init({
        elem,
        selectors: {
            toggler     : '.j-mortgage-accordion-toggler',
            content     : '.j-mortgage-accordion-content',
            contentOuter: '.j-mortgage-accordion-content-outer',
            blockElement: '.j-mortgage-card-block'
        }
    });
};

// Шаг назад
export const stepBack = (back) => {
    back.addEventListener('click', (event) => {
        event.preventDefault();

        if (history.length === 1) {
            window.location.href = window.location.origin;
        } else {
            history.back();
        }
    });
};

// Шеринг
export const initShare = (shareElements) => {
    shareElements.forEach((element) => {
        new Share().init({
            target: element
        });
    });
};

// Инициализация меню с горизонтальным скроллом
export const initHorizontalMenu = (menu) => {
    const activeItem = menu.querySelector('.is-active');

    if (!activeItem) {
        return;
    }

    const scrollToActiveItem = () => {
        const menuPadding = parseInt(window.getComputedStyle(menu).getPropertyValue('padding-left'));

        menu.scrollTo({
            left    : activeItem.offsetLeft - menuPadding,
            behavior: 'smooth'
        });
    };

    scrollToActiveItem();

    window.addEventListener('orientationchange', () => {
        setTimeout(scrollToActiveItem, 0);
    });
};

// Инициализация кастомного слайдера swiper first-screen slider
export const initSwiperSlider = (options) => {
    const wrapper = options.wrapper;
    const sliderWrapper = wrapper.querySelector('.j-slider-slides');
    const arrowLeft = wrapper.querySelector('.j-arrow-left');
    const arrowRight = wrapper.querySelector('.j-arrow-right');
    const dots = wrapper.querySelector('.j-slider-dots');

    const slider = new SwiperSlider({
        item    : sliderWrapper,
        settings: {
            wrapperClass    : 'j-slider-slides__wrapper',
            slideClass      : 'j-slider-slides__slide',
            slideActiveClass: 'is-active',
            slideNextClass  : 'is-next',
            slidesPerView   : 'auto',
            loop            : false,
            speed           : 1100,
            effect          : 'creative',
            creativeEffect  : {
                prev: {
                    scale    : 1.1,
                    translate: ['-20%', 0, 0]
                },
                next: {
                    translate: ['100%', 0, 0]
                }
            },
            navigation: {
                prevEl: arrowLeft,
                nextEl: arrowRight
            },
            pagination: {
                el       : dots,
                clickable: true
            },
            autoplay: {
                delay: 5000
            }
        }
    });

    slider.init();
};

// Инициализация кастомного слайдера swiper slider with Mousewheel control
const verticalSliderSettings = {
    preventInteractionOnTransition: false,
    direction                     : 'vertical',
    slidesPerView                 : 1,
    touchReleaseOnEdges           : true,
    spaceBetween                  : 0,
    speed                         : 900,
    effect                        : 'creative',
    creativeEffect                : {
        prev: {
            shadow   : true,
            scale    : 1.1,
            translate: [0, '-10%', 0]
        },
        next: {
            translate: [0, '100%', 0]
        }
    },
    mousewheel: {
        enabled    : false,
        forceToAxis: true
    }
};

/* eslint-disable  */
const verticalSliderMousewheel = (slider, selector) => {
    const sliderContainers = document.querySelectorAll(selector);
    const scrollBySlider = new ScrollBy();
    let cachedEntry = null;
    let scrollCapture = false;
    let isUserTouched = false;
    let touchYStart = null;

    let scrollSpeed = 0;

    const checkScrollSpeed = (() => {
        const delay = 50;
        let lastPos = null;
        let newPos = null;
        let timer = null;
        let delta = 0;

        const clear = () => {
            lastPos = null;
            delta = 0;
        };

        return () => {
            newPos = window.scrollY;
            if (lastPos !== null) { // && newPos < maxScroll
                delta = newPos - lastPos;
            }
            lastPos = newPos;
            clearTimeout(timer);
            timer = setTimeout(clear, delay);

            return delta;
        };
    })();

    window.onscroll = () => {
        scrollSpeed = checkScrollSpeed();
    };

    const scrollBySection = (delta) => {
        const sliderEdge = delta > 0 ? 'isEnd' : 'isBeginning';
        const sliderAction = delta > 0 ? 'slideNext' : 'slidePrev';
        const userGoTop = slider.slider.isBeginning && sliderEdge === 'isBeginning';
        const userGoBottom = slider.slider.isEnd && sliderEdge === 'isEnd';

        if (!scrollCapture && (userGoTop || userGoBottom)) {
            slider.slider.params.mousewheel.releaseOnEdges = true;
            Utils.bodyStatic();
        } else if (!slider.slider[sliderEdge]) {
            if (cachedEntry?.target && !cachedEntry?.target.classList.contains('in-progress')) {
                Utils.bodyFixed(cachedEntry.target);
                scrollBySlider.scrollToElement(cachedEntry.target);
            }

            slider.slider[sliderAction]();
        }
    }

    const scrollSliderBySection = throttle(slider.slider.params.speed, (event) => {
        if (slider.slider.isProcessing) {
            return;
        }

        scrollBySection(event.deltaY);
    });

    const getTouchStart = (event) => {
        isUserTouched = true;
        touchYStart = event.touches[0]?.clientY;
    };

    const getTouchEnd = () => {
        isUserTouched = false;
    };

    const touchScrollSliderBySection = (event) => {
        if (slider.slider.isProcessing || !isUserTouched || !touchYStart) {
            return
        }

        const delta = touchYStart - (event.touches[0]?.clientY || 0);

        scrollBySection(delta);

        touchYStart = null;
    };

    const operateSlider = (entry) => {
        cachedEntry = entry;

        if (entry.isIntersecting) {
            if (entry.target.dataset.vertical) {
                entry.target.classList.add('in-progress');
                Utils.bodyFixed(entry.target);
                slider.slider.disable();
                setTimeout(() => {
                    scrollBySlider.init({
                        speedAnimation: 300,
                        position: 'bottom'
                    });
                    scrollBySlider.scrollToElement(entry.target);
                    setTimeout(() => {
                        slider.slider.enable();
                    }, 900);
                    entry.target.classList.remove('in-progress');
                }, 300);
            }
            slider.slider.mousewheel.enable();
            slider.slider.on('slideChange', () => {
                setTimeout(() => {
                    slider.slider.params.mousewheel.releaseOnEdges = false;
                }, 500);
            });
            slider.slider.on('reachEnd', () => {
                setTimeout(() => {
                    slider.slider.params.mousewheel.releaseOnEdges = true;
                    Utils.bodyStatic();
                }, slider.slider.params.speed);
            });
            slider.slider.on('reachBeginning', () => {
                setTimeout(() => {
                    slider.slider.params.mousewheel.releaseOnEdges = true;
                    Utils.bodyStatic();
                }, slider.slider.params.speed);
            });
            slider.slider.on('touchMove', () => {
                scrollCapture = true;
            });
            slider.slider.on('scroll', () => {
                scrollCapture = true;
            });
            slider.slider.on('transitionStart', () => {
                slider.slider.disable();
            });
            slider.slider.on('transitionEnd', () => {
                slider.slider.update();
                setTimeout(() => {
                    slider.slider.enable();
                }, 50);
            })

            document.addEventListener('wheel', scrollSliderBySection, {passive: false});
            document.addEventListener('touchstart', getTouchStart, {passive: false});
            document.addEventListener('touchmove', touchScrollSliderBySection, {passive: false});
            document.addEventListener('touchend', getTouchEnd, {passive: false});
        } else {
            slider.slider.mousewheel.disable();

            document.removeEventListener('wheel', scrollSliderBySection);
            document.removeEventListener('touchstart', getTouchStart);
            document.removeEventListener('touchmove', touchScrollSliderBySection);
            document.removeEventListener('touchend', getTouchEnd);

            scrollCapture = false;
            isUserTouched = false;
            touchYStart = null;
        }
    }

    const verticalSliderObserver = new IntersectionObserver(([entry]) => {
        // для десктопа и быстрой прокрутки
        if (Utils.isMobile() && Math.abs(scrollSpeed) < 100) {
            return;
        }

        operateSlider(entry, Math.abs(scrollSpeed) > 100);
    }, {
        threshold: Utils.isMobile() ? 0.001 : 0.6
    });

    const verticalSliderObserverMobileSpeed = new IntersectionObserver(([entry]) => {
        // для медленной прокрутки (мобилки)
        if (Math.abs(scrollSpeed) >= 100) {
            return;
        }

        operateSlider(entry);
    }, {
        threshold: 0.55
    });

    sliderContainers.forEach((sliderContainer) => {
        if (Utils.isMobile()) {
            verticalSliderObserverMobileSpeed.observe(sliderContainer);
        }
        return verticalSliderObserver.observe(sliderContainer);
    });
};
/* eslint-enable */

export const initSwiperVerticalSlider = (options) => {
    const wrapper = options.wrapper;
    const sliderWrapper = wrapper.querySelector('.j-slider-slides');
    const dots = wrapper.querySelector('.j-slider-dots');

    const slider = new SwiperSlider({
        item    : sliderWrapper,
        settings: {
            wrapperClass    : 'j-slider-slides__wrapper',
            slideClass      : 'j-slider-slides__slide-vertical',
            slideActiveClass: 'is-active',
            slideNextClass  : 'is-next',
            loop            : false,
            allowTouchMove  : true,
            ...verticalSliderSettings,
            navigation      : {
                prevEl: wrapper.querySelector('.j-arrow-up'),
                nextEl: wrapper.querySelector('.j-arrow-down')
            },
            pagination: {
                el               : dots,
                clickable        : true,
                bulletClass      : 'swiper-vertical-pagination-bullet',
                bulletActiveClass: 'is-active',
                renderBullet(index, className) {
                    return `<span class="${className}">${index + 1}</span>`;
                }
            }
        }
    });

    slider.init();
};

// Инициализация кастомного слайдера swiper carousel slider
export const initSwiperCarouselSlider = (options) => {
    const wrapper = options.wrapper;

    const isDesktopEnabled = !wrapper.classList.contains('j-swiper-no-desktop');
    const sliderWrapper = wrapper.querySelector('.j-slider-slides');
    const arrowLeft = wrapper.querySelector('.j-arrow-left');
    const arrowRight = wrapper.querySelector('.j-arrow-right');
    const dots = wrapper.querySelector('.j-slider-dots');
    const dotsType = dots && dots.dataset.type ? dots.dataset.type : 'bullets';
    const initialSlide = options.initialSlide || 0;

    const slider = new SwiperSlider({
        item    : sliderWrapper,
        settings: {
            enabled         : Utils.isMobile() || isDesktopEnabled,
            wrapperClass    : 'j-slider-slides__wrapper',
            slideClass      : 'j-slider-slides__slide-carousel',
            slideActiveClass: 'is-active',
            slideNextClass  : 'is-next',
            slidesPerView   : 'auto',
            loop            : false,
            speed           : 500,
            initialSlide,
            navigation      : {
                prevEl: arrowLeft,
                nextEl: arrowRight
            },
            pagination: {
                el       : dots,
                type     : dotsType,
                clickable: true
            }
        }
    });

    slider.init();
};

// Инициализация кастомного слайдера swiper carousel slider
/* eslint-disable */
export const initSwiperRelatedSlider = (options) => {
    const wrapper = options.wrapper;
    const mainWrapper = options.mainWrapper;
    const secondWrapper = options.secondWrapper;
    const mainSliderWrapper = mainWrapper.querySelector('.j-slider-slides');
    const secondSliderWrapper = secondWrapper.querySelector('.j-slider-slides');
    const navList = options.wrapper.querySelectorAll('.related-slider__nav-item');
    const arrowPrev = secondWrapper.querySelector('.j-arrow-left') ?? wrapper.querySelector('.j-arrow-up');
    const arrowNext = secondWrapper.querySelector('.j-arrow-right') ?? wrapper.querySelector('.j-arrow-down');
    const dots = secondWrapper.querySelector('.j-slider-dots');
    const dotsType = dots && dots.dataset.type ? dots.dataset.type : 'bullets';
    const verticalSettings = options.isVertical ? verticalSliderSettings : null;
    const mainSettings = {
        wrapperClass       : 'j-slider-slides__wrapper',
        slideClass         : 'j-slider-slides__slide-related',
        slideActiveClass   : 'is-active',
        slideNextClass     : 'is-next',
        slidesPerView      : 'auto',
        loop               : false,
        touchReleaseOnEdges: true,
        speed              : 500,
        allowTouchMove     : false
    };

    const slidesNum = [...secondSliderWrapper.querySelectorAll(`.${mainSettings.slideClass}`)].length;

    const secondSlider = new SwiperSlider({
        item    : secondSliderWrapper,
        settings: {
            ...mainSettings,
            ...verticalSettings,
            effect    : 'slide',
            navigation: {
                prevEl: arrowPrev,
                nextEl: arrowNext
            },
            pagination: {
                el       : dots,
                type     : dotsType,
                clickable: true
            }
        }
    });

    secondSlider.init();

    if (slidesNum < 2) {
        const arrowWrapper = secondSlider?.slider?.navigation?.nextEl?.closest('.j-slider-nav');
        arrowWrapper.classList.add('is-disabled');
    }

    const sliderMain = new SwiperSlider({
        item    : mainSliderWrapper,
        settings: {
            ...mainSettings,
            ...verticalSettings,
            effect    : options.isVertical ? 'creative' : 'fade',
            controller: {
                control: secondSlider.slider
            }
        }
    });

    sliderMain.init();
    secondSlider.slider.controller.control = sliderMain.slider;

    if (options.hasDynamicBg && secondSlider?.slider) {
        const bgWrapper = options.wrapper.querySelector('.j-background-slider');
        const bgSliderWrapper = bgWrapper.querySelector('.j-slider-slides');
        let paginationActiveIndex = 0;

        const bgSlider = new SwiperSlider({
            item    : bgSliderWrapper,
            settings: {
                ...mainSettings,
                slideClass: 'j-background-slider__slide',
                allowClick: true,
                effect    : 'slide'
            }
        });

        bgSlider.init();

        secondSlider.slider.on('paginationUpdate', (swiper) => {
            const direction = swiper.activeIndex > paginationActiveIndex ? 'slideNext' : 'slidePrev';
            let neededSlide = null;

            switch (direction) {
                case 'slideNext':
                    neededSlide = sliderMain?.slider?.slides[sliderMain?.slider?.activeIndex];
                    break;
                case 'slidePrev':
                    neededSlide = sliderMain?.slider?.slides[sliderMain?.slider?.activeIndex + 1];
                    break;
                default: return;
            }

            if (!neededSlide.dataset?.bgChange) {
                paginationActiveIndex = swiper.activeIndex;
                return;
            }

            bgSlider.slider[direction]();
            paginationActiveIndex = swiper.activeIndex;
        });
    }

    if (navList) {
        secondSlider.slider.on('slideChange', () => {
            navList.forEach(item => item.classList.remove('active'));
            navList[secondSlider.slider.realIndex].classList.add('active');
        });

        navList.forEach((navItem, i) => {
            if (i === 0) {
                navItem.classList.add('active');
            }
            navItem.addEventListener('click', () => {
                secondSlider.slider.slideTo(i);
            })
        });
    }
};
/* eslint-enable */

// Инициализация слайдера для acceptance-steps
/* eslint-disable */
export const initSwiperAcceptanceStepsSlider = (options) => {
    const wrapper = options.wrapper;
    const sliderWrapper = wrapper.querySelector('.j-slider-slides');
    const arrowLeft = wrapper.querySelector('.j-arrow-left');
    const arrowRight = wrapper.querySelector('.j-arrow-right');
    const dots = wrapper.querySelector('.j-slider-dots');

    const slider = new SwiperSlider({
        item                : sliderWrapper,
        settings            : {
            enabled         : true,
            wrapperClass    : 'acceptance-steps__block',
            slideClass      : 'acceptance-steps__item',
            // loop            : false,
            // speed           : 500,
            slidesPerView: 'auto',
            navigation      : {
                prevEl      : arrowLeft,
                nextEl      : arrowRight
            },
            pagination      : {
                el          : dots,
                clickable   : true,
                bulletClass : 'acceptance-steps__bullet',
                bulletActiveClass: 'acceptance-steps__bullet-active',
                renderBullet(index, className) {
                    return `<div class="${className}" title="Слайд ${index + 1}"></div>`;
                }
            },
            breakpoints: {
                960: {
                    enabled: false
                },
            }
        }
    });

    slider.init();
};
/* eslint-enable */

// Инициализация дефолтных туглеров
export const initToggle = (options) => {
    const toggler = new Toggle();

    toggler.init({
        wrapper: options.wrapper
    });
};

// Инициализация хода строительства
export const initProgress = (options) => {
    const progress = new Progress();

    progress.init({
        wrapper: options.wrapper
    });
};

// Инициализация новостей
export const initNewsList = (options) => {
    const newsList = new NewsList();

    newsList.init({
        wrapper: options.wrapper
    });
};

// Инициализация кастомного слайдера swiper actions slider
export const initSwiperActionsSlider = (options) => {
    const wrapper = options.wrapper;
    const sliderWrapper = wrapper.querySelector('.j-slider-slides');
    const arrowsWrapper = wrapper.querySelector('.j-slider-actions__nav');
    const dots = wrapper.querySelector('.j-slider-dots');

    const slider = new SwiperSlider({
        item    : sliderWrapper,
        settings: {
            wrapperClass    : 'j-slider-slides__wrapper',
            slideClass      : 'j-slider-actions__slide',
            slideActiveClass: 'is-active',
            slideNextClass  : 'is-next',
            direction       : 'horizontal',
            slidesPerView   : 1,
            spaceBetween    : 8,
            loop            : false,
            speed           : 500,
            navigation      : {
                prevEl: arrowsWrapper.querySelector('.j-arrow-left'),
                nextEl: arrowsWrapper.querySelector('.j-arrow-right')
            },
            pagination: {
                el       : dots,
                clickable: true
            }
        }
    });

    slider.init();
};

// Инициализация кастомного слайдера swiper flats slider
export const initSwiperFlatsSlider = (options) => {
    const wrapper = options.wrapper;
    const sliderWrapper = wrapper.querySelector('.j-slider-slides');
    const arrowLeft = wrapper.querySelector('.j-arrow-left');
    const arrowRight = wrapper.querySelector('.j-arrow-right');

    const slider = new SwiperSlider({
        item    : sliderWrapper,
        settings: {
            wrapperClass    : 'j-slider-slides__wrapper',
            slideClass      : 'j-slider-slides__slide',
            slideActiveClass: 'is-active',
            slideNextClass  : 'is-next',
            direction       : 'horizontal',
            slidesPerView   : 'auto',
            loop            : false,
            speed           : 500,
            navigation      : {
                prevEl: arrowLeft,
                nextEl: arrowRight
            }
        }
    });

    slider.init();
};

// Инициализация слайдера при наведении в объектах
export const initObjectSlider = (options) => {
    new ObjectSlider().init(options);
};

observer.subscribe('sliderObjects:refresh', (container) => {
    const arrObjects = Array.from(container.querySelectorAll('.j-object'));

    arrObjects.forEach((object) => {
        initObjectSlider({
            wrapper: object
        });
    });
});

// Параметрический
export const initParametric = (options) => {
    new Parametric(options).init();
};

// Типовые квартиры
export const initFlatType = (options) => {
    new FlatType(options).init();
};

// Появление попапа с социальными сетями при клике по кнопке "Поделиться"
export const initButtonShare = (buttonsArray) => {
    const buttonShare = new ButtonShare();

    buttonsArray.forEach((elem) => {
        buttonShare.init({
            target: elem
        });
    });
};

// Инициализация паралакса для галереи
export const initParallaxColumn = (wrapper) => {
    const parallax = new ParallaxColumn();

    parallax.init({
        wrapper
    });
};

// Связанные селекты
export const initBindedSelects = (prime, dependent) => {
    if (!prime || !dependent) {
        return;
    }

    const primeSelect = new CustomSelect({
        elem: prime
    });

    const dependentSelect = new CustomSelect({
        elem: dependent
    });

    primeSelect.init();
    dependentSelect.init();

    observer.subscribe('custom-select:change', () => {
        const chosenOptionValue = prime.querySelector('.j-custom-select__value').innerHTML;
        const dependentOptions = Array.from(dependent.querySelectorAll('.j-custom-select-item'));

        dependentOptions.forEach((item) => {
            const method = item.dataset.bindedValue === chosenOptionValue ? 'remove' : 'add';

            item.classList[method]('is-invisible');
        });
    });
};

/**
 * Инициализация модуля показа списка акций на мобильных
 * @param {*} stocksList - массив элементов плашек Акции
 */
export const initStocks = (stocksList) => {
    stocksList.forEach((stock) => {
        const options = {
            wrapper: stock
        };

        new Stock(options).init();
    });
};

// Инициализация кнопок избранного
export const initFavorite = (elems) => {
    const favorites = new FavoriteButton();

    favorites.init({
        favoriteButtons: elems
    });
};


export const initFavoriteButton = () => {
    const favoriteButtons = Array.from(document.querySelectorAll('.j-favorite:not([data-init])'));

    initFavorite(favoriteButtons);
};


observer.subscribe('favorite:reInit', () => {
    initFavoriteButton();
});

// Инициализация раздела избранного
export const initFavoritesList = (wrapper) => {
    const favorites = new FavoritesList();

    favorites.init({wrapper});
};

// Инициализация избранного в шапке
export const initFavoriteState = (wrapper) => {
    const favoriteState = new FavoriteState();

    favoriteState.init({wrapper});
};

// Инициализация раздела сравнения
export const initCompareList = (wrapper) => {
    const compare = new CompareList();

    compare.init({wrapper});
};

// Инициализация кнопок Сравнения
export const initCompare = (elems) => {
    const compare = new CompareButton();

    compare.init({
        compareButtons: elems
    });
};

export const initCompareButton = () => {
    const compareButtons = Array.from(document.querySelectorAll('.j-compare:not([data-init])'));

    initCompare(compareButtons);
};

observer.subscribe('compare:reInit', () => {
    initCompareButton();
});

// Инициализация сравнения в шапке
export const initCompareState = (wrapper) => {
    const compareState = new CompareState();

    compareState.init({wrapper});
};

// Инициализация кастомного слайдера swiper carousel slider
export const initSwiperComparelSlider = (options) => {
    const wrapper = options.wrapper;

    const sliderWrapper = wrapper.querySelector('.j-slider-slides');
    const initialSlide = options.initialSlide || 0;

    const wrapperClass = Utils.isMobile() ?
        `.${wrapper.classList.value.replace(/\s/g, '.')}` :
        '.j-slider-nav--desktop';

    const slider = new SwiperSlider({
        item    : sliderWrapper,
        settings: {
            enabled         : true,
            wrapperClass    : 'j-slider-slides__wrapper',
            slideClass      : 'j-compare__result-item',
            slideActiveClass: 'is-active',
            slideNextClass  : 'is-next',
            slidesPerView   : Utils.isMobile() ? 1 : 'auto',
            spaceBetween    : Utils.isMobile() ? 20 : 0,
            pagination      : false,
            loop            : false,
            speed           : 500,
            initialSlide,
            navigation      : {
                prevEl: `${wrapperClass} .j-arrow-left`,
                nextEl: `${wrapperClass} .j-arrow-right`
            },
            on: {
                afterInit: (instance) => {
                    if (!instance.isEnd) {
                        return;
                    }

                    instance.navigation.nextEl.style.display = 'none';
                    instance.navigation.prevEl.style.display = 'none';
                }
            }
        }
    });

    slider.init();

    return slider;
};


export const initComparePopups = () => {
    const popupReserveButtons = Array.from(document.querySelectorAll('.j-popup-reserve-compare:not([data-init])'));

    if (popupReserveButtons.length) {
        initCallbackPopup(popupReserveButtons);

        popupReserveButtons.forEach((button) => {
            button.dataset.init = true;
        });
    }
};

export const initPostMessagePopup = () => {
    return new PostMessagePopup({
        closeButtonAriaLabel: 'Закрыть'
    }).init();
};

export const initStickyButton = (wrapper) => {
    return new StickyButton(wrapper).init();
};

export const initOpkForm = () => {
    return new MultiStepForm();
};

export const initPromotionTerms = (promotionTerms) => {
    const options = {
        wrapper: promotionTerms
    };

    new PromotionsTerms(options).init();
};
